/**
 * useSessionSSE Hook
 * Bridges SSEManager events to sessionReducer dispatch actions.
 * Used inside SessionContext to keep session state as the single source of truth.
 */

import { useEffect, useRef, useCallback, useContext } from 'react';
import { getIdToken } from 'firebase/auth';
import { useQueryClient } from '@tanstack/react-query';
import { SSEManager } from '../services/sse-manager';
import { getFirebaseAuth } from '../lib/firebase';
import type { SessionAction } from '../contexts/sessionReducer';
import { clearHistoryCache } from '../views/session/CommandInputArea';
import { NotificationContext } from '../contexts/NotificationContext';
import type { 
  TextDeltaEvent, 
  ArtifactUpdateEvent, 
  ArtifactDeleteEvent, 
  MessagePartUpdatedEvent, 
  StatusUpdateEvent, 
  PermissionAskedEvent, 
  QuestionAskedEvent,
  VaultFileSkippedEvent
} from '../utils/event-parser';

const formatStagingError = (err: string): string => {
  if (err === 'FILE_TOO_LARGE') return 'File size exceeds the 10MB limit.';
  if (err === 'PII_MASKING_FAILED') return 'PII masking check failed on the customer utility.';
  if (err === 'FILE_STAGING_FAILED') return 'Temporary storage staging failed.';
  if (err === 'UNAUTHORIZED') return 'You do not have permission to access this file.';
  return err || 'Unknown staging issue.';
};

export function useSessionSSE(
  activeSessionIds: string[],
  dispatch: React.Dispatch<SessionAction>
): {
  connect: (sessionId: string) => void;
  disconnect: (sessionId: string) => void;
  reconnect: (sessionId: string) => void;
} {
  const queryClient = useQueryClient();
  const notifyContext = useContext(NotificationContext);
  const showWarning = notifyContext?.showWarning || ((message: string) => {
    if (import.meta.env.MODE !== 'test') {
      console.warn(`[useSessionSSE] ${message}`);
    }
  });
  const managersRef = useRef<Map<string, SSEManager>>(new Map());

  useEffect(() => {
    // 1. Clean up managers for sessions that are no longer active
    managersRef.current.forEach((manager, id) => {
      if (!activeSessionIds.includes(id)) {
        manager.disconnect();
        managersRef.current.delete(id);
      }
    });

    // 2. Create and connect managers for new active sessions
    activeSessionIds.forEach((sessionId) => {
      if (!managersRef.current.has(sessionId)) {
        const manager = new SSEManager(sessionId, {
          getToken: async () => {
            // In mock-auth dev mode, use the mock token so local dev still works
            if (import.meta.env.VITE_MOCK_AUTH === 'true') {
              return (window as any).__MOCK_TOKEN__ ?? 'valid-token';
            }
            // Production: get a fresh Firebase ID token (auto-refreshed by SDK)
            const auth = getFirebaseAuth();
            const currentUser = auth.currentUser;
            if (!currentUser) {
              return null;
            }
            return getIdToken(currentUser);
          },
        });

        manager.on('open', () => {
          dispatch({ type: 'UPDATE_STATUS', payload: { sessionId, status: 'busy' } });
        });

        manager.on('closed', () => {
          dispatch({ type: 'UPDATE_STATUS', payload: { sessionId, status: 'idle' } });
        });

        manager.on('error', (event?: any) => {
          const errMsg = event?.message || 'Connection error';
          dispatch({ type: 'SET_ERROR', payload: { sessionId, message: errMsg } });
        });

        manager.on('textDelta', (event) => {
          const textDeltaEvent = event as TextDeltaEvent;
          dispatch({ type: 'ADD_DELTA', payload: { sessionId, delta: textDeltaEvent.delta } });
        });

        manager.on('message.part.updated', (event) => {
          const msgEvent = event as MessagePartUpdatedEvent;
          dispatch({ type: 'ADD_DELTA', payload: { sessionId, delta: msgEvent.part.text } });
        });

        manager.on('done', () => {
          dispatch({ type: 'UPDATE_STATUS', payload: { sessionId, status: 'idle' } });
          queryClient.invalidateQueries({ queryKey: ['dashboard', 'init'] });
        });

        manager.on('thinkingStart', () => {
          dispatch({ type: 'SET_THINKING', payload: { sessionId, thinking: true } });
        });

        manager.on('thinkingEnd', () => {
          dispatch({ type: 'SET_THINKING', payload: { sessionId, thinking: false } });
        });

        manager.on('artifactUpdate', (event) => {
          const artifactEvent = event as ArtifactUpdateEvent;
          dispatch({
            type: 'ADD_ARTIFACT',
            payload: {
              sessionId,
              file: {
                fileId: artifactEvent.name,
                filename: artifactEvent.name,
                sizeBytes: 0,
                type: 'output',
                createdAt: new Date().toISOString(),
              },
            },
          });
        });

        manager.on('artifactDelete', (event) => {
          const deleteEvent = event as ArtifactDeleteEvent;
          dispatch({ type: 'REMOVE_ARTIFACT', payload: { sessionId, fileId: deleteEvent.name } });
        });

        manager.on('statusUpdate', (event) => {
          const statusEvent = event as StatusUpdateEvent;
          dispatch({
            type: 'UPDATE_STATUS',
            payload: {
              sessionId,
              status: 'waiting',
              turnCount: statusEvent.turnCount,
              maxTurns: statusEvent.maxTurns,
            },
          });
        });

        manager.on('sessionTerminated', () => {
          dispatch({ type: 'UPDATE_STATUS', payload: { sessionId, status: 'complete' } });
        });

        manager.on('summaryGenerating', () => {
          dispatch({ type: 'UPDATE_STATUS', payload: { sessionId, status: 'summarizing' } });
        });

        manager.on('summaryComplete', () => {
          dispatch({ type: 'UPDATE_STATUS', payload: { sessionId, status: 'idle' } });
          queryClient.invalidateQueries({ queryKey: ['dashboard', 'init'] });
          clearHistoryCache();
        });

        manager.on('summaryFailed', (event: any) => {
          dispatch({ type: 'UPDATE_STATUS', payload: { sessionId, status: 'idle' } });
          dispatch({ type: 'SET_ERROR', payload: { sessionId, message: `Chat summary failed: ${event.error || 'unknown'}` } });
        });

        manager.on('permission.asked', (event) => {
          const permEvent = event as PermissionAskedEvent;
          dispatch({
            type: 'SET_ACTIVE_PERMISSION',
            payload: { sessionId, permission: permEvent },
          });
        });

        manager.on('question.asked', (event) => {
          const questionEvent = event as QuestionAskedEvent;
          dispatch({
            type: 'SET_ACTIVE_QUESTION',
            payload: { sessionId, question: questionEvent },
          });
        });

        manager.on('vault_file_skipped', (event) => {
          const skipEvent = event as VaultFileSkippedEvent;
          showWarning(`Vault file "${skipEvent.filename}" was skipped: ${formatStagingError(skipEvent.error)}`);
        });

        managersRef.current.set(sessionId, manager);
      }
    });
  }, [activeSessionIds.join(','), dispatch]);

  useEffect(() => {
    return () => {
      managersRef.current.forEach((manager) => {
        manager.disconnect();
      });
      managersRef.current.clear();
    };
  }, []);

  const connect = useCallback((sessionId: string) => {
    managersRef.current.get(sessionId)?.connect();
  }, []);

  const disconnect = useCallback((sessionId: string) => {
    managersRef.current.get(sessionId)?.disconnect();
  }, []);

  const reconnect = useCallback((sessionId: string) => {
    managersRef.current.get(sessionId)?.connect();
  }, []);

  return { connect, disconnect, reconnect };
}
